import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { TbMail, TbMapPin } from 'react-icons/tb';
import TechVisual from './TechVisual';
import LogoIcon from '../../../components/atoms/LogoIcon/LogoIcon';

const HeroSectionWrapper = styled.section`
  height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
  padding: 0 4rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 70% 50%, ${({ theme }) => theme.colors.primary.main}15 0%, transparent 70%);
    z-index: -1;
  }

  @media (max-width: 1024px) {
    padding: 0 2rem;
  }

  @media (max-width: 768px) {
    height: auto;
    min-height: 100vh;
    padding: 100px 1.5rem 40px;
    flex-direction: column-reverse;
    overflow-y: auto;
  }
`;

const LayoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 4rem;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 1400px;
  z-index: 1;
  margin: 0 auto;

  @media (max-width: 1024px) {
    gap: 2rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }

  @media (max-width: 400px) {
    gap: 1.5rem;
  }
`;

const VisualSide = styled(motion.div)`
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    max-width: 80%;
    margin: 0 auto;
  }

  @media (max-width: 400px) {
    max-width: 100%;
  }
`;

const ContentSide = styled(motion.div)`
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  
  @media (max-width: 768px) {
    text-align: center;
    align-items: center;
  }
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const HeroLogoWrapper = styled.div`
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 80px !important;
    height: 80px !important;
  }
  
  @media (max-width: 1200px) {
    width: 65px;
    height: 65px;
    
    svg {
      width: 65px !important;
      height: 65px !important;
    }
  }
  
  @media (max-width: 768px) {
    width: 55px;
    height: 55px;
    
    svg {
      width: 55px !important;
      height: 55px !important;
    }
  }
  
  @media (max-width: 400px) {
    width: 45px;
    height: 45px;
    
    svg {
      width: 45px !important;
      height: 45px !important;
    }
  }
`;

const HeroTitle = styled(motion.h1)`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 5.5rem;
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: 0;
  line-height: 0.9;
  letter-spacing: -0.04em;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.text.primary} 0%, ${({ theme }) => theme.colors.primary.main} 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  
  @media (max-width: 1200px) {
    font-size: 4.5rem;
  }
  
  @media (max-width: 768px) {
    font-size: 3.5rem;
  }

  @media (max-width: 400px) {
    font-size: 2.75rem;
  }
`;

const HeroDescription = styled(motion.p)`
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1.25rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: 2.5rem;
  line-height: 1.5;
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  max-width: 500px;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 2rem;
    max-width: 100%;
  }

  @media (max-width: 400px) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const RequestDemoButton = styled(motion.a)`
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.default};
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 1rem;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: 1rem 3rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  margin-bottom: 4rem;
  text-decoration: none;
  display: inline-block;
  
  &:hover {
    background: ${({ theme }) => theme.colors.primary.main};
    color: white;
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 768px) {
    width: 100%;
    margin-bottom: 2.5rem;
    box-sizing: border-box;
    text-align: center;
  }

  @media (max-width: 400px) {
    padding: 0.875rem 2rem;
    margin-bottom: 2rem;
  }
`;

const ContactInfo = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  border-left: 2px solid ${({ theme }) => theme.colors.primary.main}30;
  padding-left: 1.5rem;
  
  @media (max-width: 768px) {
    align-items: center;
    border-left: none;
    padding-left: 0;
    gap: 1rem;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-family: ${({ theme }) => theme.fontFamily.primary};
  font-size: 0.9rem;
  transition: color 0.3s ease;
  
  svg {
    color: ${({ theme }) => theme.colors.primary.main};
    opacity: 0.8;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const HeroSection = () => {
  return (
    <HeroSectionWrapper>
      <LayoutGrid>
        <VisualSide
          initial={{ opacity: 0, x: -60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        >
          <TechVisual />
        </VisualSide>

        <ContentSide
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 1.2, ease: "easeOut", delay: 0.3 }}
        >
          <TitleContainer>
            <HeroLogoWrapper>
              <LogoIcon />
            </HeroLogoWrapper>
            <HeroTitle>SenseAI</HeroTitle>
          </TitleContainer>

          <HeroDescription>
            Privacy-First Contactless Sensing Technology empowering longevity with dignity through advanced thermal analysis.
          </HeroDescription>

          <RequestDemoButton
            href="mailto:contact@sense-ai.org?subject=Demo Request - SenseAI"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            Request Demo
          </RequestDemoButton>

          <ContactInfo
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
          >
            <ContactItem>
              <TbMail size={22} />
              <span>contact@sense-ai.org</span>
            </ContactItem>
            <ContactItem>
              <TbMapPin size={22} />
              <span>Hong Kong</span>
            </ContactItem>
          </ContactInfo>
        </ContentSide>
      </LayoutGrid>
    </HeroSectionWrapper>
  );
};

export default HeroSection;
